import { Button, Checkbox, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

export type InternalDiffusionTeam = {
  id: number;
  name: string;
  native: boolean;
  pdf: boolean;
};

export type InternalDiffusionGrid = {
  canTransmit: boolean;
  hasNative: boolean;
  hasPdf: boolean;
  teams: InternalDiffusionTeam[];
};

export type InternalDiffusionPopupProps = {
  open: boolean;
  onClose: () => void;
  yardId: string;
  documentId: number;
  versionId: number;
  documentName: string;
  versionIndex: string | null;
  /** Called after a successful transmission so the list can refresh. */
  onTransmitted: () => void;
};

type GridState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; grid: InternalDiffusionGrid };

type Format = "native" | "pdf";

const cellKey = (teamId: number, format: Format) => `${teamId}:${format}`;

/**
 * Grille de transmission interne (écran 015) : équipes du même contrat
 * que l'utilisateur × formats (natif / PDF). Les cases déjà en possession sont
 * cochées et verrouillées ; pour un document piloté, les cases restantes
 * peuvent être cochées pour transmettre la version aux équipes choisies.
 */
export function InternalDiffusionPopup({
  open,
  onClose,
  yardId,
  documentId,
  versionId,
  documentName,
  versionIndex,
  onTransmitted,
}: InternalDiffusionPopupProps) {
  const [state, setState] = useState<GridState>({ status: "loading" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setState({ status: "loading" });
    setSelected(new Set());

    async function fetchGrid() {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/internal-diffusion",
        {
          params: {
            path: {
              yardId,
              documentId: String(documentId),
              versionId: String(versionId),
            },
          },
        },
      );

      if (cancelled) return;
      if (isNotNil(error) || isNil(data)) {
        setState({ status: "error" });
        return;
      }
      setState({ status: "success", grid: data.data });
    }

    fetchGrid();
    return () => {
      cancelled = true;
    };
  }, [open, yardId, documentId, versionId]);

  const toggleCell = (teamId: number, format: Format) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const key = cellKey(teamId, format);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleTransmit = async () => {
    if (state.status !== "success" || selected.size === 0) return;

    const cellsByTeam = new Map<number, { native: boolean; pdf: boolean }>();
    for (const key of selected) {
      const [teamIdPart, format] = key.split(":");
      const teamId = Number(teamIdPart);
      const cell = cellsByTeam.get(teamId) ?? { native: false, pdf: false };
      if (format === "native") cell.native = true;
      if (format === "pdf") cell.pdf = true;
      cellsByTeam.set(teamId, cell);
    }

    setSubmitting(true);
    const { error } = await apiClient.POST(
      "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/internal-diffusion",
      {
        params: {
          path: {
            yardId,
            documentId: String(documentId),
            versionId: String(versionId),
          },
        },
        body: {
          cells: [...cellsByTeam.entries()].map(([teamId, cell]) => ({
            teamId,
            native: cell.native,
            pdf: cell.pdf,
          })),
        },
      },
    );
    setSubmitting(false);

    if (isNotNil(error)) {
      toaster.error({
        title: "Transmission impossible",
        description: error.error ?? "Une erreur est survenue",
      });
      return;
    }

    toaster.success({
      title: "Version transmise",
      description:
        "Les conducteurs de travaux des équipes concernées et le directeur travaux ont été notifiés",
    });
    onTransmitted();
    onClose();
  };

  const grid = state.status === "success" ? state.grid : null;

  return (
    <Modal.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="lg">
          <Modal.Header>
            <Modal.Title>Transmission interne</Modal.Title>
          </Modal.Header>

          <div className="px-6 pb-6">
            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              {documentName}
              {versionIndex ? ` - Indice ${versionIndex}` : ""}
            </p>

            {state.status === "loading" && (
              <p className="text-sm text-zinc-500">Chargement...</p>
            )}

            {state.status === "error" && (
              <p className="text-sm text-red-600">Erreur lors du chargement</p>
            )}

            {isNotNil(grid) && grid.teams.length === 0 && (
              <p className="text-sm text-zinc-500">
                Aucune équipe de votre contrat sur ce chantier.
              </p>
            )}

            {isNotNil(grid) && grid.teams.length > 0 && (
              <>
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-700">
                      <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                        Équipe
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                        Natif
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                        PDF
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {grid.teams.map((team) => (
                      <tr
                        key={team.id}
                        className="border-b border-zinc-100 dark:border-zinc-700/50"
                      >
                        <td className="px-3 py-2 text-zinc-800 dark:text-zinc-200">
                          {team.name}
                        </td>
                        {(["native", "pdf"] as const).map((format) => {
                          const available =
                            format === "native" ? grid.hasNative : grid.hasPdf;
                          const possessed =
                            format === "native" ? team.native : team.pdf;
                          if (!available) {
                            return (
                              <td
                                key={format}
                                className="px-3 py-2 text-center text-zinc-300 dark:text-zinc-600"
                              >
                                -
                              </td>
                            );
                          }
                          return (
                            <td key={format} className="px-3 py-2 text-center">
                              <Checkbox
                                checked={
                                  possessed ||
                                  selected.has(cellKey(team.id, format))
                                }
                                disabled={possessed || !grid.canTransmit}
                                onCheckedChange={() =>
                                  toggleCell(team.id, format)
                                }
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {grid.canTransmit ? (
                  <div className="mt-6 flex items-center justify-end gap-3">
                    <Button onClick={onClose} className="text-sm">
                      Annuler
                    </Button>
                    <Button
                      onClick={handleTransmit}
                      disabled={selected.size === 0 || submitting}
                      className="text-sm"
                    >
                      {submitting ? "En cours..." : "Transmettre"}
                    </Button>
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-zinc-500">
                    Seuls les pilotes de ce document peuvent transmettre de
                    nouveaux formats.
                  </p>
                )}
              </>
            )}
          </div>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}
